import { default as React, useState } from 'react';
import { DeploymentOption, RegionSelect } from '../../../models';
import {
  generateRegionSelectData,
  loadingRegionSelect,
  useGetVisibleRegions,
} from '../../../utils/RegionSelect';
import { BaseCardProps } from '../shared/shared';
import { isEqual, mapValues, sortBy } from 'lodash';
import { Cloud, DeploymentMetadata, SignupEventType, FeatureFlag } from '@signup/shared';
import { getDeploymentsList, isFeatureEnabled } from '../../../utils/configurations';
import { useCoordinates } from '../forms/SignupCard/UseCoordinates';
import { sortAvailableRegionsByDistance } from '../../../utils/SortAvailableRegionsByDistance';
import { useTelemetry } from '@snowflake/core-ui';
import { SignupEventName } from '../../../utils/SignupLogger';
import { useSignupFlowType } from '../../../utils/useSignupFlowType';

const AWS_OHIO = 'us-east-2';
const AWS_OVERRIDE_SOURCES = new Set(['us-west-2', 'us-east-1']);

/**
 * If the nearest AWS region is Oregon (us-west-2) or N. Virginia (us-east-1)
 * and Ohio (us-east-2) is available and enabled in the list, reorder the AWS
 * region list so Ohio is at position 0. Other regions keep their distance order.
 * Returns the new list plus metadata about what (if anything) was overridden.
 */
export function applyOhioDefaultOverride(regionSelectList: RegionSelect): {
  regionSelectList: RegionSelect;
  awsPreOverrideDefault: string;
  awsOverrideApplied: boolean;
} {
  const awsRegions = regionSelectList[Cloud.AWS] ?? [];
  const preOverrideDefault = awsRegions[0]?.value ?? '';

  if (!AWS_OVERRIDE_SOURCES.has(preOverrideDefault)) {
    return {
      regionSelectList,
      awsPreOverrideDefault: preOverrideDefault,
      awsOverrideApplied: false,
    };
  }

  const ohioIndex = awsRegions.findIndex(r => r.value === AWS_OHIO && !r.disabled);
  if (ohioIndex <= 0) {
    return {
      regionSelectList,
      awsPreOverrideDefault: preOverrideDefault,
      awsOverrideApplied: false,
    };
  }

  const ohio = awsRegions[ohioIndex];
  const reorderedAws = [
    ohio,
    ...awsRegions.slice(0, ohioIndex),
    ...awsRegions.slice(ohioIndex + 1),
  ];

  return {
    regionSelectList: { ...regionSelectList, [Cloud.AWS]: reorderedAws },
    awsPreOverrideDefault: preOverrideDefault,
    awsOverrideApplied: true,
  };
}

export function useDeploymentMetadata({
  props,
  email,
  filterVisibleRegions,
}: {
  props: BaseCardProps;
  filterVisibleRegions: boolean;
  email: string; // TODO(huayang): this can be removed in v2.
}): {
  showVisibleRegionsFilter: boolean;
  regionSelectList: RegionSelect;
  regionSelectListSortedByDistance: boolean;
  visibleRegions: DeploymentMetadata[] | undefined;
} {
  const signupFlowType = useSignupFlowType();
  const [regionSelectList, setRegionSelectList] = useState<RegionSelect>(
    props.signupParams.listing
      ? loadingRegionSelect
      : generateRegionSelectData({ signupCardProps: props, email, signupFlowType }),
  );
  const [regionSelectListSortedByDistance, setRegionSelectListSortedByDistance] = useState(false);

  const { logEvent } = useTelemetry();

  // Track if we've already logged the default region event to prevent duplicates
  const hasLoggedDefaultRegion = React.useRef(false);

  const [showVisibleRegionsFilter, setShowVisibleRegionsFilter] = useState(true);

  const coordinates = useCoordinates();

  const {
    visibleRegions,
    errorFetchingVisibleRegions,
  }: {
    visibleRegions: DeploymentMetadata[] | undefined;
    errorFetchingVisibleRegions: boolean;
  } = useGetVisibleRegions(props.signupParams.listing);

  React.useEffect(() => {
    // If the region is determined in the url, the logic in this hook does not need to run
    if (props.signupParams.region || props.signupParams.regionp) {
      return;
    }

    // If we just have the default regions, this switch effectively does nothing so we hide it.
    // We only check the region names, since they shouldn't change between dev/prod environments whereas the deployments might.
    // We also have to compare sorted arrays, since they could have different orders and thus would fail the equality check.
    const visibleRegionNames = sortBy(visibleRegions?.map(region => region.regionName));
    const defaultRegionNames = sortBy(getDeploymentsList().map(region => region.regionName));

    const allRegionsVisible = isEqual(visibleRegionNames, defaultRegionNames);
    const shouldHideWhenAllVisible =
      allRegionsVisible &&
      !isFeatureEnabled(FeatureFlag.FF_ENABLE_REGION_FILTER_WHEN_ALL_REGIONS_VISIBLE);

    if (errorFetchingVisibleRegions || shouldHideWhenAllVisible) {
      setShowVisibleRegionsFilter(false);
    }
  }, [
    visibleRegions,
    errorFetchingVisibleRegions,
    props.signupParams.region,
    props.signupParams.regionp,
  ]);

  React.useEffect(() => {
    // If the region is determined in the url, the logic in this hook does not need to run
    if (props.signupParams.region || props.signupParams.regionp) {
      return;
    }

    // Exit early if no email AND no coordinates (nothing to do)
    if (!email && !coordinates?.length) {
      return;
    }

    // Update the list if an email has been entered, run a check for the AWS Mumbai block
    // If no email yet but coordinates exist, still generate list for sorting
    let updatedRegionSelectList = email
      ? generateRegionSelectData({ signupCardProps: props, email, signupFlowType })
      : generateRegionSelectData({ signupCardProps: props, email: '', signupFlowType });
    let sorted = false;

    // Update the region select list if
    // the visibleRegions state has loaded
    // or the customer toggles the filterVisibleRegions switch
    if (visibleRegions && filterVisibleRegions) {
      updatedRegionSelectList = generateRegionSelectData({
        signupCardProps: props,
        visibleRegions,
        email: email || '',
        signupFlowType,
      });
    } else if (visibleRegions && !filterVisibleRegions) {
      updatedRegionSelectList = generateRegionSelectData({
        signupCardProps: props,
        email: email || '',
        signupFlowType,
      });
    }

    // Sort the region select if
    // the coordinates of the user's location has loaded
    if (coordinates?.length) {
      // We sort the available regions if this isn't a listing bundle signup
      // Or if it is, then if the visibleRegions has loaded
      if (!props.signupParams.listing || visibleRegions) {
        updatedRegionSelectList = sortAvailableRegionsByDistance(
          updatedRegionSelectList,
          coordinates,
        );
        sorted = true;

        const overrideResult = isFeatureEnabled(FeatureFlag.FF_ENABLE_OHIO_DEFAULT_OVERRIDE)
          ? applyOhioDefaultOverride(updatedRegionSelectList)
          : {
              regionSelectList: updatedRegionSelectList,
              awsPreOverrideDefault: updatedRegionSelectList[Cloud.AWS]?.[0]?.value ?? '',
              awsOverrideApplied: false,
            };
        updatedRegionSelectList = overrideResult.regionSelectList;

        // Log the default we computed for each cloud provider (only once to prevent duplicate events)
        if (!hasLoggedDefaultRegion.current) {
          const defaultRegions = mapValues(
            updatedRegionSelectList,
            // first region in sorted list is closest, default to '' if missing
            (regions: DeploymentOption[]) => regions?.[0]?.value ?? '',
          );
          logEvent({
            event: SignupEventName.SIGNUP_COMPUTE_DEFAULT_REGION,
            type: SignupEventType.UI_RESPONSE_SUCCESS,
            data: {
              ...defaultRegions,
              awsPreOverrideDefault: overrideResult.awsPreOverrideDefault,
              awsOverrideApplied: overrideResult.awsOverrideApplied,
            },
            interaction: false,
          });
          hasLoggedDefaultRegion.current = true;
        }
      }
    }

    setRegionSelectListSortedByDistance(sorted);
    setRegionSelectList(updatedRegionSelectList);
  }, [email, visibleRegions, filterVisibleRegions, coordinates, props, logEvent, signupFlowType]);

  return {
    showVisibleRegionsFilter,
    regionSelectList,
    visibleRegions,
    regionSelectListSortedByDistance,
  };
}
